/**
 * Text measurement for note layout (packages/scene/src/text.ts), on a canvas
 * with the same bundled fonts the card is drawn with. Widths are measured at
 * 100px and scaled, since type width grows linearly with size.
 */
import { useEffect, useState } from 'react';
import type { Measure } from '@foldmycard/scene';

import '@fontsource/noto-sans/400.css';
import '@fontsource/noto-sans/400-italic.css';
import '@fontsource/noto-sans/700.css';
import '@fontsource/noto-sans/700-italic.css';
import '@fontsource/noto-serif/400.css';
import '@fontsource/noto-serif/400-italic.css';
import '@fontsource/noto-serif/700.css';
import '@fontsource/noto-serif/700-italic.css';
import '@fontsource/caveat/400.css';
import '@fontsource/caveat/700.css';
import '@fontsource/noto-naskh-arabic/400.css';
import '@fontsource/noto-naskh-arabic/700.css';
import '@fontsource/noto-sans-hebrew/400.css';
import '@fontsource/noto-sans-hebrew/700.css';

let ctx: CanvasRenderingContext2D | null = null;
const cache = new Map<string, number>();

export const measure: Measure = (text, style) => {
  const font = `${style.italic ? 'italic ' : ''}${style.bold ? 700 : 400} 100px ${style.font}`;
  const key = `${font}|${text}`;
  let w = cache.get(key);
  if (w === undefined) {
    ctx = ctx || document.createElement('canvas').getContext('2d')!;
    ctx.font = font;
    w = ctx.measureText(text).width;
    cache.set(key, w);
  }
  return (w / 100) * style.sizeMm;
};

/**
 * Changes every time the browser finishes loading fonts, so layouts measured
 * with a fallback font are redone with the real one.
 */
export function useFontsVersion(families: string[]): number {
  const [version, setVersion] = useState(0);
  const want = families.join('|');
  useEffect(() => {
    let live = true;
    const bump = () => { if (live) { cache.clear(); setVersion((v) => v + 1); } };
    Promise.all(families.flatMap((f) => [document.fonts.load(`400 20px ${f}`, 'Aאع'), document.fonts.load(`700 20px ${f}`, 'Aאع')]))
      .then(bump, bump);
    document.fonts.addEventListener('loadingdone', bump);
    return () => { live = false; document.fonts.removeEventListener('loadingdone', bump); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [want]);
  return version;
}
